import auth0, { Auth0DecodedHash, Auth0Error, WebAuth } from 'auth0-js';
import * as types from 'src/types';
import luminaEnv from 'src/env';
import logoutLocally from 'src/utils/logoutLocally';
import isTokenExpired from 'src/utils/tokenValidate';
import { decodeAuth0Token } from 'src/utils/tokenProcess';

let auth0Client: WebAuth | null = null; // Gets set below

const checkSessionPromise = () => new Promise<Auth0DecodedHash>((resolve, reject) => {
    if (!auth0Client) reject(new Error('AUTH0_CLIENT_NOT_INITIALISED'));
    auth0Client?.checkSession(
        { state: btoa(luminaEnv.appUrl) },
        (error: Auth0Error | null, result: Auth0DecodedHash) => {
            if (error) reject(error);
            resolve(result);
        }
    );
});

export const initialiseAuth = (auth0InitParams: {
    appUrl: string;
    auth0: types.auth0InitParam;
}) => {
    auth0Client = new auth0.WebAuth({
        domain: auth0InitParams.auth0.domain,
        clientID: auth0InitParams.auth0.clientId,
        redirectUri: `${auth0InitParams.appUrl}/callback`,
        scope: auth0InitParams.auth0.scope,
        responseType: 'token',
    });

    return auth0Client;
};

export const getExistingToken = () => {
    const token = localStorage.getItem('id_token');

    if (!token) return null;

    if (isTokenExpired(token)) {
        logoutLocally();
        return null;
    }

    return token;
};

const storeAuth0Session = (authResult: Auth0DecodedHash) => {
    logoutLocally();

    const token = authResult.idToken as string;
    localStorage.setItem('id_token', token);

    // Use entire token payload as the profile
    const profile = decodeAuth0Token(token);

    // Note, we previously did auth0.getUserInfo(authResult.accessToken
    // Now, we don't because we request profile in the scope, we store decoded jwt in profile instead, for convenience
    localStorage.setItem('profile', JSON.stringify(profile));

    return token;
};

export const renewAuth = async () => {
    if (!auth0Client) {
        throw new Error('AUTH0_CLIENT_NOT_INITIALISED');
    }

    const auth0Session = await checkSessionPromise();
    const token = storeAuth0Session(auth0Session);

    return token;
};

export const runAuthManager = async () => {
    let token = getExistingToken();

    if (!token) {
        // Not logged in, check single sign on
        token = await renewAuth();
    }

    return token;
};

export default {
    init: initialiseAuth,
    authenticate: runAuthManager,
    getExistingToken,
    renewAuth,
};
